import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Row,
  Col,
  Input,
  Button,
  Typography,
  Modal,
  message as Message,
} from 'antd';
import { FaEnvelope } from 'react-icons/fa';
import i18n from 'i18next';

import { AuthenticationRouteProps } from './Authentication';
import ForgotPasswordIcon from '../../assets/images/png/forgot-password.png';
import EmailSentIcon from '../../assets/images/png/email-sent.png';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { passwordResetEmail } from '../../store/auth/actions';
import { getAppGlobalState } from '../../store/app-globals/selectors';
import { setError, setLoading } from '../../store/app-globals/actions';

const { Text } = Typography;

export interface ModalContentProps {
  icon: string;
  description: string;
  onClick?: (e?: React.MouseEvent) => void;
  onStay?: (e?: React.MouseEvent) => void;
}

export const EmailSentModalContent: React.FC<ModalContentProps> = ({
  icon,
  description,
  onClick,
}) => {
  return (
    <div className="modal-content-wrapper">
      <div className="modal-content-icon">
        <img src={icon} alt="modal-content-icon" />
      </div>
      <div className="modal-content-description">
        <span>{description}</span>
      </div>
      <div className="button-container">
        <Button
          block
          type="primary"
          onClick={onClick}
          shape="round"
          className="continue-button continue-button-primary"
        >
          <span className="continue-button-text">{i18n.t('Continue')}</span>
        </Button>
      </div>
    </div>
  );
};

export const PasswordUpdatedContent: React.FC<ModalContentProps> = ({
  icon,
  description,
  onClick,
  onStay,
}) => {
  return (
    <div className="modal-content-wrapper">
      <div className="modal-content-icon">
        <img src={icon} alt="modal-content-icon" />
      </div>
      <div className="modal-content-description">
        <span>{description}</span>
      </div>
      <div className="button-container">
        <Button
          block
          type="primary"
          onClick={onClick}
          shape="round"
          className="continue-button continue-button-primary"
        >
          <span className="continue-button-text">{i18n.t('Logout')}</span>
        </Button>
        <Button
          block
          type="primary"
          onClick={onStay}
          shape="round"
          className="continue-button continue-button-primary"
        >
          <span className="continue-button-text">{i18n.t('STAY_LOGGED')}</span>
        </Button>
      </div>
    </div>
  );
};

export const ForgotPassword: React.FC<AuthenticationRouteProps> = ({
  setHeaderTitle,
}) => {
  const { hasError, isLoading, message } = useSelector(getAppGlobalState);
  const [email, setEmail] = useState('');
  const [disabled, setDisabled] = useState(true);
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleFieldChange = (e: React.FormEvent<HTMLInputElement>) => {
    setEmail(e.currentTarget.value);
  };

  const validate: React.EffectCallback = () => {
    setDisabled(email === '');
  };

  const onChangeAccountType: React.EffectCallback = () => {
    if (setHeaderTitle) setHeaderTitle(t('FORGOT_PASSWORD'));
  };

  const confirmationSentModal = React.useCallback(() => {
    const modal = Modal.info({
      icon: null,
      centered: true,
      className: 'modal-content-container modal-content-description',
      okButtonProps: { style: { display: 'none' } },
      content: (
        <EmailSentModalContent
          icon={EmailSentIcon}
          description={t('PASSWORD_SENT')}
          onClick={() => {
            setTimeout(() => {
              modal.destroy();
              history.goBack();
            }, 500);
          }}
        />
      ),
    });
  }, [history, t]);

  const handleSendButton = () => {
    dispatch(passwordResetEmail(email));
  };

  useEffect(() => {
    if (hasError) {
      Message.error(i18n.t('Something went wrong'));
      dispatch(setError(false, ''));
      return;
    }
    if (!isLoading && message && !hasError) {
      dispatch(setLoading(false, ''));
      setEmail('');
      confirmationSentModal();
    }
  }, [isLoading, message, hasError, dispatch, confirmationSentModal]);

  useEffect(onChangeAccountType);
  useEffect(validate, [email]);
  return (
    <Row className="login" justify="space-between">
      <Col
        span={24}
        flex="0 0 200px"
        className="user-icon-container padded-top"
      >
        <img src={ForgotPasswordIcon} alt="email-login-icon" />
      </Col>
      <Row>
        <Col span={24} flex="0 0 200px" className="forgot-password-text">
          <Typography>
            <Text>{t('TO_RESET_PASSWORD')}</Text>
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col span={24} className="input-field-container">
          <Input
            size="large"
            placeholder="E-mail"
            prefix={<FaEnvelope color="rgba(255,255,255,0.7)" />}
            className="input-field"
            value={email}
            onChange={handleFieldChange}
          />
        </Col>
      </Row>
      <Col span={24} className="login-button-container padded-bottom">
        <Button
          shape="round"
          className="login-button login-button-secondary"
          disabled={disabled}
          onClick={handleSendButton}
          loading={isLoading}
        >
          {!isLoading ? t('Send') : message}
        </Button>
      </Col>
    </Row>
  );
};
